import { X, FileText } from "lucide-react";
import { useState, type ReactNode } from "react";
import { StyleSheet, View, Text, Pressable, Platform, ActivityIndicator } from "react-native";
import { spacing } from "../../../../styles/design-system";

export interface AttachmentChipProps {
  type: "task" | "assignment" | "file";
  name: string;
  duedate?: string | number | Date;
  removeChip?: () => void;
  isLoading?: boolean;
}

const getIcon = (type: "task" | "assignment" | "file") => {
  switch (type) {
    case "task":
      return "📋";
    case "assignment":
      return "📝";
    case "file":
      return null; // Will render FileText icon instead
  }
};

export const AttachmentChip = ({ type, name, duedate, removeChip, isLoading = false }: AttachmentChipProps) => {
  const [isHover, setIsHover] = useState(false);
  const icon = getIcon(type);

  return (
    <View style={styles.attachmentChip}>
      {icon ? <Text style={styles.icon}>{icon}</Text> : <FileText size={16} color="#667085" />}
      <Text style={styles.name}>{name}</Text>
      {duedate != null && (
        <Text style={styles.duedate}>
          {`· ${new Date(duedate).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "2-digit" })}`}
        </Text>
      )}
      {isLoading && <ActivityIndicator size="small" color="#667085" />}
      {!isLoading && removeChip != null && (
        <Pressable
          style={styles.removeIconWrapper}
          onPress={removeChip}
          onMouseEnter={() => {
            setIsHover(true);
          }}
          onMouseLeave={() => {
            setIsHover(false);
          }}>
          <X size={20} color={isHover ? "#475467" : "#98a2b3"} strokeWidth={3} />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  attachmentChip: {
    width: "fit-content",
    display: "flex",
    flexDirection: "row",
    gap: 8,
    marginBottom: spacing.xs,
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#eaecf0",
    ...Platform.select({
      ios: {
        shadowColor: "rgb(16, 24, 40)",
        shadowOffset: {
          width: 0,
          height: 1,
        },
        shadowOpacity: 0.16,
        shadowRadius: 2.5,
      },
      android: {
        elevation: 3,
      },
      default: {
        boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      },
    }),
  },
  icon: {
    fontSize: 16,
  },
  name: {
    color: "#101828",
    fontSize: 14,
  },
  duedate: {
    fontSize: 12,
    color: "#667085",
  },
  removeIconWrapper: {
    padding: 2,
  },
});
